import React, { useEffect, useRef, useState } from "react";
import { DocumentPlusIcon } from "@heroicons/react/24/outline";
import Alert from "../Alert";
import ActionButton from "../ActionButton";
import StartOverButton from "./StartOverButton";
import InlineLinkButton from "./InlineLinkButton";
import {
  KEY_TYPES,
  DEFAULT_KEY_TYPE_ID,
  EAB_REGEX,
  EAB_MIN_LENGTH,
  EAB_MAX_LENGTH,
  MAX_DOMAINS_PER_CERTIFICATE,
  DOMAIN_REGEX,
  EAB_REJECTED_CODE,
  // 2026-08-14 go-public review ("validation-drift") : the same
  // legacy standard-base64 normalisation the server applies before
  // EAB_REGEX - see validateEab below.
  normaliseBase64Url,
} from "../../../shared/issuance/config";
// 2026-06-01 : real Top-Level Domain (TLD) validation via tldts,
// shared with the server (validateInputs in
// src/server/services/issuance/validation.js) so the browser and
// server agree on what counts as a real domain.
import { isRegistrablePublicDomain } from "../../../shared/utils/domainValidation";
import {
  apiCreateSession,
  apiStartIssuance,
  ApiError,
  LONG_CALL_TIMEOUT_MS,
  withTimeoutSignal,
} from "../../issuance/apiClient";

function parseDomains(raw) {
  return raw
    .split(/[\s,;]+/)
    .map((d) => d.trim().toLowerCase())
    .filter((d) => d.length > 0);
}

// Strip the "*." wildcard prefix before applying DOMAIN_REGEX, exactly
// the way the server-side validateInputs does it (see
// src/server/services/issuance/validation.js). The shared DOMAIN_REGEX in
// src/shared/issuance/config.js expects the base hostname only - wildcard
// handling is the caller's job. Previously this component kept its
// own local regex that accepted "*." as part of the pattern ; the
// duplicated copy was the exact drift risk the shared-constants
// module was put there to prevent.
//
// 2026-06-01 : the former RESERVED_DOMAIN_SUFFIXES blocklist is
// replaced by isRegistrablePublicDomain (Public Suffix List via
// tldts), which AFFIRMATIVELY requires a real ICANN Top-Level
// Domain (TLD). Same realness gate the server applies.
function validateDomain(d) {
  const base = d.startsWith("*.") ? d.slice(2) : d;
  if (!DOMAIN_REGEX.test(base)) return false;
  if (!isRegistrablePublicDomain(base)) return false;
  return true;
}

function validateEab(value) {
  if (value.length < EAB_MIN_LENGTH || value.length > EAB_MAX_LENGTH) {
    return false;
  }
  // Normalise legacy standard-base64 "+" / "/" to the base64url
  // alphabet BEFORE the regex, exactly as the server's validateInputs
  // does (2026-08-14 go-public review, "validation-drift" : without
  // this the browser rejected - with a misleading "BASE64 CHARACTERS
  // ONLY" message - the very legacy credentials the server-side
  // normalisation deliberately accepts). The RAW value is what the
  // form submits ; the server normalises again authoritatively, so
  // the translation here exists only to make the two validators
  // agree. Length is checked on the raw value above : normalisation
  // is character-for-character, so the two lengths are identical.
  return EAB_REGEX.test(normaliseBase64Url(value));
}

/**
 * Step 2 - Configure SSL Certificate.
 *
 * When loadedConfig is present (Configuration File drop on Step 1)
 * the form pre-fills with the saved credentials and the server
 * reuses the saved ACME account at startIssuance time. A file that
 * carries an order URL opens in resume mode, with the domain list and
 * key type locked to the saved order ; Start a Reissue (or a file
 * with no order URL) unlocks them so the customer can issue a
 * different SSL Certificate under the same account.
 *
 * On submit :
 *   1. validate inputs locally (cheap, no server round-trip)
 *   2. POST /api/issuance/sessions  → server validates, fetches the
 *      ACME directory, returns a sessionId
 *   3. POST /api/issuance/sessions/:id/start → server generates keys,
 *      registers the account (unless reusing), creates the order,
 *      fetches every authorization
 *   4. hand the page the session id, the four fields of the public
 *      session view it renders from (status, authorizations, domains,
 *      primaryDomain) and the chosen key type
 *
 * onReset - the page-level Start Over handler (handleReset in
 * src/client/pages/Issuance.jsx). Operator directive 2026-07-05 :
 * every section carries its own centered Continue + Start Over row,
 * so the trigger moved from the page bottom into each step component ;
 * the handler and its discard/cleanup logic stay on the page.
 */
function Step2Configure({
  serverId,
  loadedConfig,
  onComplete,
  onSessionCreated,
  onReset,
}) {
  const initial = loadedConfig
    ? {
        domainsRaw: (loadedConfig.domains || []).join("\n"),
        keyTypeId: loadedConfig.certKeyTypeId || DEFAULT_KEY_TYPE_ID,
        eabKid: loadedConfig.eab.kid,
        eabHmac: loadedConfig.eab.hmacKey,
      }
    : {
        domainsRaw: "",
        keyTypeId: DEFAULT_KEY_TYPE_ID,
        eabKid: "",
        eabHmac: "",
      };

  // Issue-new-with-this-account switch (2026-08-14 operator ruling :
  // account reuse must be REACHABLE). Every file this tool exports
  // carries an orderUrl, so before this switch existed every drop
  // entered resume mode locked to the old order : a customer
  // returning weeks later for a REPLACEMENT SSL Certificate could
  // only re-download the old one, while the Step 4 copy promised the
  // file worked "for any later reissue". Flipping this discards the
  // order pinning for THIS submission only - the account credentials
  // from the file are reused, the fields unlock (prefilled from the
  // file, since a replacement usually covers the same domains), and
  // the server creates a fresh order under the saved account.
  const [startNewWithAccount, setStartNewWithAccount] = useState(false);

  // Resume mode : the dropped Configuration File carried an in-progress
  // order URL (and the customer has not chosen issue-new above). The
  // domains, key type, and account are all fixed by that existing
  // order, so the form is locked and we simply reattach.
  const isResume =
    !!(loadedConfig && loadedConfig.orderUrl) && !startNewWithAccount;

  const [formData, setFormData] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [progressMessage, setProgressMessage] = useState("");
  const [topError, setTopError] = useState(null);
  // The machine-readable code behind topError, when the server sent
  // one : the wrong-service hint under the error Alert renders only
  // for EAB_REJECTED_CODE (see the shared config for why).
  const [topErrorCode, setTopErrorCode] = useState(null);

  // Mounted guard - pattern-sweep partner to ChallengePanel,
  // Step1Service and Step4Download. handleSubmit awaits
  // two sequential server round-trips (apiCreateSession + apiStartIssuance)
  // that together can take several seconds ; the customer pressing
  // Reset or refreshing mid-flight must not trigger setState on the
  // unmounted component.
  const mountedRef = useRef(true);
  // 2026-08-13 go-public cleanup (audit : "error-recovery") : the
  // session created by the LAST submit, kept so a retry after a
  // failed /start reattaches to it instead of minting a second
  // session. The stored fingerprint is the exact JSON payload that
  // created it - see the reuse guard in handleSubmit for why a
  // changed form must NOT reuse (stale inputs are baked into the
  // stored session server-side).
  const reusableSessionRef = useRef(null);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Continue Saved Request (operator ruling 2026-10-02) : going back
  // to resume mode puts the domain list and key type back to the
  // values in the configuration file and clears the domain field
  // error. Both fields lock again in that mode, and handleSubmit sends
  // formData together with the saved order URL and the saved private
  // key, so anything typed while the reissue switch was on must not
  // ride along : the locked form shows and sends exactly what the
  // saved order covers. The key type has no field error of its own to
  // clear.
  const handleContinueSavedRequest = () => {
    setStartNewWithAccount(false);
    setFormData((prev) => ({
      ...prev,
      domainsRaw: initial.domainsRaw,
      keyTypeId: initial.keyTypeId,
    }));
    setErrors((prev) => ({ ...prev, domainsRaw: "" }));
  };

  // Every error string surfaced in form validation goes via this
  // function. Plain sentences (operator ruling 2026-08-27) ; matches
  // ChainAnalyzer.jsx, CSRGenerator.jsx, and every other tool page
  // on the site.
  const validateForm = (parsedDomains) => {
    const next = {};
    if (parsedDomains.length === 0) {
      next.domainsRaw =
        "Please enter the domain names the SSL Certificate will cover.";
    } else if (parsedDomains.length > MAX_DOMAINS_PER_CERTIFICATE) {
      next.domainsRaw =
        "An SSL Certificate can cover at most " +
        MAX_DOMAINS_PER_CERTIFICATE +
        " domain names.";
    } else {
      const invalid = parsedDomains.filter((d) => !validateDomain(d));
      if (invalid.length > 0) {
        next.domainsRaw =
          "This does not look like a valid domain name. Please check " +
          invalid.slice(0, 3).join(", ") +
          (invalid.length > 3 ? "..." : ".");
      }
    }
    if (!loadedConfig) {
      // Fresh-issuance path requires EAB. Reuse path uses saved creds.
      if (!formData.eabKid.trim()) {
        next.eabKid =
          "The External Account Binding (EAB) key identifier is required. Copy it from your order e-mail or the order details page.";
      } else if (!validateEab(formData.eabKid.trim())) {
        next.eabKid =
          "This does not look like a key identifier. Please copy the whole value again from your order e-mail or the order details page.";
      }
      if (!formData.eabHmac.trim()) {
        next.eabHmac =
          "The External Account Binding (EAB) HMAC key is required. Copy it from your order e-mail or the order details page.";
      } else if (!validateEab(formData.eabHmac.trim())) {
        next.eabHmac =
          "This does not look like an HMAC key. Please copy the whole value again from your order e-mail or the order details page.";
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTopError(null);
    setTopErrorCode(null);

    const domains = parseDomains(formData.domainsRaw);
    if (!validateForm(domains)) return;

    const keyTypeChoice = KEY_TYPES.find((k) => k.id === formData.keyTypeId);
    if (!keyTypeChoice) {
      setTopError("The selected SSL Certificate key type is not valid.");
      return;
    }

    setLoading(true);
    try {
      setProgressMessage("Creating Issuance Session...");
      const payload = {
        serverId,
        domains,
        keyTypeId: keyTypeChoice.id,
      };
      if (loadedConfig) {
        // The order pin and the saved SSL Certificate Private Key
        // travel TOGETHER (2026-08-14 go-public review). On a RESUME the
        // saved key is REQUIRED : the order may already be finalized
        // against it, and the server rejects orderUrl-without-key up
        // front for exactly that reason. On a NEW order (the
        // issue-new-with-this-account switch, or an account-only
        // file) the saved key must NOT be sent : the server's reuse
        // branch would issue the REPLACEMENT SSL Certificate against
        // the SAME Private Key as the expiring one - the opposite of
        // what a replacement is for - and, with the key-type selector
        // unlocked in this mode, a changed selection would hand e.g.
        // an RSA PEM to an ECC import and crash /start. Omitting it
        // makes the server generate a fresh key of the SELECTED type.
        const keepOrderPin = !startNewWithAccount && !!loadedConfig.orderUrl;
        payload.existing = {
          accountKeyPem: loadedConfig.accountKeyPem,
          accountUrl: loadedConfig.accountUrl,
          certKeyPem: keepOrderPin
            ? loadedConfig.certPrivateKeyPem || undefined
            : undefined,
          // When the saved file carries an order URL this is a RESUME :
          // the server reattaches to that exact order at Sectigo®
          // instead of creating a new one. The issue-new-with-this-
          // account switch (2026-08-14 operator ruling) deliberately
          // drops the pin so the saved account gets a FRESH order.
          orderUrl: keepOrderPin ? loadedConfig.orderUrl : undefined,
        };
        // The server still accepts EAB on the reuse path for
        // round-tripping into the next Configuration File export.
        payload.eab = {
          kid: loadedConfig.eab.kid,
          hmacKey: loadedConfig.eab.hmacKey,
        };
      } else {
        payload.eab = {
          kid: formData.eabKid.trim(),
          hmacKey: formData.eabHmac.trim(),
        };
      }
      // 2026-08-13 go-public cleanup (audit : "error-recovery") : a
      // retry after a FAILED /start reuses the session the server
      // already holds instead of minting a second one - but ONLY when
      // the submitted inputs are byte-identical to the ones that
      // created it (the fingerprint guard below). Reusing across an
      // EDIT would re-run issuance against the stale inputs baked
      // into the stored session, issuing the wrong SSL Certificate
      // order ; a changed form therefore creates a fresh session, and
      // the superseded one is DISCARDED by the page's
      // handleStep2SessionCreated orphan cleanup (the new create
      // fires onSessionCreated, which best-effort DELETEs the
      // previous pendingSessionIdRef before overwriting it - the
      // 48-hour TTL is only the backstop if that DELETE is lost -
      // the cleanup DELETE, not TTL expiry, is the mechanism).
      const payloadFingerprint = JSON.stringify(payload);
      let sessionId;
      if (
        reusableSessionRef.current &&
        reusableSessionRef.current.fingerprint === payloadFingerprint
      ) {
        sessionId = reusableSessionRef.current.sessionId;
      } else {
        // Bounded exactly like the /start call below (operator ruling
        // 2026-10-02) : every control on this form, Start Over
        // included, is disabled while this call is in flight, so a
        // connection that never answers has to end in the AbortError
        // the catch maps to the retryable timed-out message.
        const created = await withTimeoutSignal(
          LONG_CALL_TIMEOUT_MS,
          (signal) => apiCreateSession(payload, { signal })
        );
        // 2026-07-03 PR #76 sixth-pass review (Finding 2) : the apiClient
        // `call` helper resolves an off-contract 200-with-empty-body to
        // `null` (the `(json && json.data) || null` return in
        // src/client/issuance/apiClient.js:call). Reading created.sessionId on a
        // null result would throw a raw lowercase JS TypeError, which the
        // catch below surfaces verbatim into the customer-facing topError
        // Alert. Throw a house-style sentence BEFORE the property
        // access so the Alert renders that instead of the raw deref.
        // Mirrors the identical guard in the localStorage-restore path
        // (the `if (!view)` throw inside the restore useEffect in
        // src/client/pages/Issuance.jsx). The reason is
        // deliberately just the cause : the Alert title
        // "Unable to Start Issuance Request" is the header, so a
        // full sentence here would double the prefix.
        if (!created) {
          throw new Error("The server returned an empty session.");
        }
        sessionId = created.sessionId;
        reusableSessionRef.current = {
          sessionId,
          fingerprint: payloadFingerprint,
        };
        // Report the sessionId to the parent BEFORE the mounted-check
        // (2026-05-31 code review #7). The parent stores it in a ref
        // for cleanup-on-Reset. If this component happens to unmount
        // between the apiCreateSession response landing and the
        // setState commit (StrictMode replay, navigation, fast Reset
        // click) we would otherwise leave the server session orphaned
        // until the 48-hour Time To Live (TTL) sweep. Firing the
        // callback synchronously here ensures the parent's ref is
        // populated even on the narrow race window. The parent's ref
        // is independent of THIS component's mounted state ; reads
        // happen on Issuance.jsx which has its own lifecycle.
        if (typeof onSessionCreated === "function") {
          try {
            onSessionCreated(sessionId);
          } catch {
            // ignore - parent-side bookkeeping must not block issuance
          }
        }
      }
      if (!mountedRef.current) return;

      // SHORT ENOUGH FOR A BUTTON (operator, from his live run : "I saw
      // the button go disabled and change to two or 3 lines of text.
      // Whatever it is saying should not be 3 lines of text"). These
      // were 58 and 62 characters, and they render as the Continue
      // button's own label - a button is not a place for a progress
      // sentence.
      //
      // NO POSSESSIVE, on his correction : "Why can't it just say
      // Registering Account or Initializing Account". He is right, and
      // it is the site canon rather than a preference - not one button
      // label anywhere in src/client carries "Your". Every loading
      // label on the site is a bare gerund plus its object
      // ("Analyzing...", "Checking Status...", "Requesting
      // Validation..."), and the first draft here broke that pattern.
      //
      // The two states stay distinct because the distinction is the
      // useful part : registering a new account takes noticeably
      // longer than picking up a saved one, so the label explains the
      // wait the customer is looking at rather than merely occupying
      // it.
      setProgressMessage(
        loadedConfig ? "Initializing Account..." : "Registering Account..."
      );
      // 2026-08-13 go-public cleanup (audit : "functionality-wedge") :
      // a bounded AbortController on the long /start call. A hung
      // connection previously left this form in-flight forever with
      // every control disabled ; now it aborts after
      // LONG_CALL_TIMEOUT_MS (see its JSDoc in apiClient.js - the
      // server's own budget error wins whenever the server is alive)
      // and the catch below maps the AbortError to a retryable
      // house-style message. The fingerprint-guarded session reuse
      // above means the retry reattaches instead of re-creating.
      const view = await withTimeoutSignal(LONG_CALL_TIMEOUT_MS, (signal) =>
        apiStartIssuance(sessionId, { signal })
      );
      if (!mountedRef.current) return;
      // 2026-07-03 PR #76 sixth-pass review (Finding 2) : same null-view
      // guard as the apiCreateSession result above. apiStartIssuance
      // routes through the same apiClient `call` helper and can also
      // resolve to `null` on an off-contract empty body ; reading
      // view.status / view.authorizations / view.domains on null would
      // throw a raw lowercase TypeError into the customer-facing
      // topError Alert. Throw the house-style sentence BEFORE
      // any property access. Same message as the create guard above and
      // the sibling restore / resume paths.
      if (!view) {
        throw new Error("The server returned an empty session.");
      }

      // 2026-06-01 third-round code review #7, MOVED here by the
      // 2026-08-13 go-public cleanup (audit : "error-recovery") :
      // best-effort wipe of the External Account Binding (EAB)
      // credentials from React state - now AFTER issuance has started
      // successfully, not merely after session creation. Wiping on
      // create meant any /start failure forced the customer to
      // re-paste the HMAC secret before they could retry (the
      // requiredFieldsFilled gate holds Continue disabled on empty
      // EAB fields). The original wipe rationale is unchanged :
      // the session-bearer cookie carries the authorisation from here
      // on, the EAB lives only in the server-side session (and the
      // Step 4 Configuration File if downloaded), and extensions or
      // error-boundary snapshots taken after this point cannot
      // capture the values from formData.
      if (!loadedConfig) {
        setFormData((prev) => ({ ...prev, eabKid: "", eabHmac: "" }));
      }
      // The session is consumed by the completed start : no future
      // submit on this mount may reuse it.
      reusableSessionRef.current = null;

      onComplete({
        sessionId,
        status: view.status,
        authorizations: view.authorizations,
        domains: view.domains,
        primaryDomain: view.primaryDomain,
        keyType: keyTypeChoice,
      });
    } catch (err) {
      if (!mountedRef.current) return;
      // A 404 from /start proves the reusable session no longer
      // exists server-side (expired, discarded, or lost to a store
      // failover), so the fingerprint guard must NOT replay its dead
      // sessionId : without this clear, every unchanged-form retry
      // reused the dead id and failed with the same SESSION NOT
      // FOUND, unrecoverable until the customer happened to edit a
      // field or press Start Over (2026-08-14 go-public review,
      // "error-recovery"). Only the 404 is definitive ; other failures keep
      // the reuse behaviour the fingerprint guard exists for.
      if (err instanceof ApiError && err.status === 404) {
        reusableSessionRef.current = null;
      }
      // The server-side error envelope is already a brand-adjusted
      // sentence (mapAcmeError, and errorHandler leaves its case
      // alone), so we surface it verbatim. The fallback strings are
      // the only messages we may produce ourselves, sentences too.
      // AbortError is our own client-side timeout (the bounded
      // controller above) : its raw browser message is lowercase
      // developer prose, so it maps to a branded retryable line.
      const msg =
        err && err.name === "AbortError"
          ? "The issuance request timed out. Please try again."
          : err instanceof ApiError
            ? err.message
            : err && err.message
              ? err.message
              : "Unknown error.";
      setTopErrorCode(
        err instanceof ApiError &&
          err.body &&
          err.body.code === EAB_REJECTED_CODE
          ? EAB_REJECTED_CODE
          : null
      );
      setTopError(msg);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setProgressMessage("");
      }
    }
  };

  // 2026-07-04 operator consistency directive ("buttons are greyed out
  // or don't work when incomplete fields are present") : Continue stays
  // DISABLED until every REQUIRED field is non-blank - the domains
  // textarea always, plus the two External Account Binding (EAB)
  // credentials on the fresh path only (a loaded Configuration File
  // carries its own saved EAB, and in resume / reuse mode the fields
  // are pre-filled from it, so the reuse paths do not gate on the EAB
  // inputs ; the domains gate is satisfied by construction there too,
  // because parseConfigFile only accepts files with a domain list).
  // This is a PRESENCE gate only : format
  // validation (domain shape, EAB alphabet / length)
  // still runs in validateForm on submit, exactly as before, and the
  // REQUIRED branches inside validateForm stay as defence in depth
  // behind this gate (a programmatic form submit bypasses a disabled
  // button). The EAB fields SURVIVE a failed apiStartIssuance - the
  // 2026-08-13 cleanup moved the best-effort EAB wipe in handleSubmit
  // to run only after a SUCCESSFUL start - so Continue stays enabled
  // for the retry with the fields intact ; the wipe only blanks them
  // after success, where this form is about to unmount anyway.
  // (2026-08-14 comment-accuracy fix : this note previously described
  // the pre-cleanup wipe-on-create behaviour, a false model of the
  // retry path.)
  /*
    The two key dropdowns, derived from KEY_TYPES so a new entry in the
    shared config appears automatically rather than needing a second
    hand-written list here (the derive-do-not-hand-list rule).

    keyTypeId remains the ONLY submitted value ; the algorithm dropdown
    is presentation, and changing it selects that algorithm's first
    entry, which is also the recommended one for RSA.
  */
  const selectedKeyType =
    KEY_TYPES.find((k) => k.id === formData.keyTypeId) || KEY_TYPES[0];
  const selectedAlgorithm = selectedKeyType.algorithm;
  const isEcc = selectedAlgorithm === "ECC";
  // Algorithm list in KEY_TYPES order, de-duplicated. The customer
  // wording matches CSRGenerator's exactly : "RSA" / "ECC (Elliptic
  // Curve)".
  const KEY_ALGORITHMS = KEY_TYPES.reduce((acc, kt) => {
    if (!acc.some((a) => a.value === kt.algorithm)) {
      acc.push({
        value: kt.algorithm,
        label: kt.algorithm === "ECC" ? "ECC (Elliptic Curve)" : kt.algorithm,
      });
    }
    return acc;
  }, []);
  // Second dropdown : the algorithm prefix is dropped from each option
  // because the first dropdown already carries it, which is how
  // CSRGenerator words the same pair ("2048 bits (Recommended)", not
  // "RSA 2048 bits (Recommended)").
  const sizeOptions = KEY_TYPES.filter(
    (kt) => kt.algorithm === selectedAlgorithm
  ).map((kt) => ({
    ...kt,
    sizeLabel: kt.label.replace(new RegExp("^" + kt.algorithm + " "), ""),
  }));
  const handleAlgorithmChange = (e) => {
    const first = KEY_TYPES.find((kt) => kt.algorithm === e.target.value);
    if (first) {
      setFormData((prev) => ({ ...prev, keyTypeId: first.id }));
    }
  };

  const requiredFieldsFilled =
    formData.domainsRaw.trim().length > 0 &&
    (!!loadedConfig ||
      (formData.eabKid.trim().length > 0 &&
        formData.eabHmac.trim().length > 0));

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/*
        The Security Notice that used to open this form has moved back
        to the top of the page, where every sibling tool keeps it
        (operator directive 2026-08-15) - see the Alert directly under
        the header in src/client/pages/Issuance.jsx. Keeping a second
        copy here would both duplicate it and re-create the stack of
        boxes the operator rejected.

        The retention detail it carried is not lost. The configuration
        file is described as password-equivalent beside the control
        that produces it (the step-away line StepAwaySave.jsx renders
        on Steps 3 and 4), and the private key retention window is
        restated on Step 4 next to the download itself.
      */}
      {isResume ? (
        <Alert type="info" title="Saved Issuance Request Loaded">
          {/* The first sentence reads the file it was given (operator,
              2026-08-27). Step4Download.jsx writes the `certificate`
              block into the file ONLY after issuance ; the step-away
              save on Steps 3 and 4 never does. So its presence proves
              the file was saved after issuance and the "already issued"
              wording is always true when shown, while its absence
              proves nothing (a Step 3 file's order may have finished
              since), so that branch keeps the wording that holds either
              way. The tool itself never reads the block : Step 2 sends
              the server only the keys and the order URL, and Step 4
              fetches the SSL Certificate from the Certificate Authority
              (finalizeAndIssue). The test ids are how the component
              test tells the two branches apart without matching prose. */}
          {loadedConfig.certificate ? (
            <p className="mb-3" data-testid="resume-notice-issued">
              This configuration file holds an SSL Certificate that has already
              been issued, so the settings below are locked to what was saved.
              Continue to download that SSL Certificate again.
            </p>
          ) : (
            <p className="mb-3" data-testid="resume-notice-in-progress">
              This configuration file holds an issuance request that has already
              started, so the settings below are locked to what was saved.
              Continue to pick up that request where it left off or to download
              the issued SSL Certificate again.
            </p>
          )}
          {/* The reachable-reuse exit (2026-08-14 operator ruling) :
              without it a returning customer could only ever
              re-enter the finished order, never issue the
              replacement SSL Certificate the saved file was promised
              to enable.

              It is what most customers who drop a file are here for :
              a Certificate as a Service SSL Certificate lasts at most
              90 days (docs/sectigo-caas/introduction-to-caas.md)
              against a one-to-three-year license, so a customer comes
              back roughly quarterly, and usually for a reissue rather
              than a resume. So the offer sits on its own line directly
              under the state paragraph, in the "question, then link"
              shape StepAwaySave.jsx uses for its secondary action, and
              the link carries the word the customer has already met -
              "reissue" is what Step 4 told them to come back for and
              what the tracking dashboard calls it.

              A LINK, not a button (operator directive 2026-08-16) : a
              full-width outline button with a sentence on it dominated
              the box. */}
          <p className="mb-3">
            Need a New SSL Certificate?{" "}
            <InlineLinkButton
              onClick={() => setStartNewWithAccount(true)}
              disabled={loading}
            >
              Start a Reissue
            </InlineLinkButton>
          </p>
          {/* The reasons are the published ones, in the order and
              words of shop.trustico.com/pages/reissue-ssl-certificate,
              kept short here and linked for the detail (operator,
              2026-08-27 : "reading the reasons on our site that are
              already published and keeping it consistent and short").
              The link takes the same in-Alert treatment as the CAA and
              MPIC references on Step 4. */}
          <p>
            A reissue is a new SSL Certificate under your existing license with
            new validity dates, and it does not need a new order. Customers
            reissue to claim the remaining validity on a license, after a server
            move, or when a private key is lost or compromised. Your saved
            account is reused, so you do not need to enter your credentials
            again. See the{" "}
            <a
              href="https://shop.trustico.com/pages/reissue-ssl-certificate"
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-medium"
            >
              Trustico® SSL Certificate Reissue
            </a>{" "}
            page for more detail.
          </p>
        </Alert>
      ) : (
        loadedConfig && (
          <Alert type="info" title="Ready to Reissue">
            <p className={loadedConfig.orderUrl ? "mb-3" : ""}>
              The account saved in this configuration file is reused, so you do
              not need to enter your credentials again. The domain names and key
              type are filled in from the file and can be changed. A new private
              key is generated for the new SSL Certificate. Check the settings
              below, then Continue.
            </p>
            {/* Symmetric escape from the issue-new switch : the saved
                order is untouched until submit, so offering the way
                back costs nothing and prevents a mis-click from
                stranding the customer outside their resume. */}
            {loadedConfig.orderUrl && startNewWithAccount && (
              <p>
                Changed Your Mind?{" "}
                <InlineLinkButton
                  onClick={handleContinueSavedRequest}
                  disabled={loading}
                >
                  Continue Saved Request
                </InlineLinkButton>
              </p>
            )}
          </Alert>
        )
      )}

      {/*
        ONE grid for every field, exactly as the sibling forms are
        built (see the single `grid grid-cols-1 md:grid-cols-2 gap-6`
        wrapping the whole body of src/client/pages/CSRGenerator.jsx,
        with full-width fields opting out through md:col-span-2).

        This replaces three separately spaced blocks - a full-width
        domains field, then two independent two-column grids - whose
        gaps were maintained by hand and did not stay equal. A single
        grid means one gap value, gap-6, above and below every field
        on the step.
      */}
      {/*
        LABEL WEIGHT (operator directive 2026-08-16 : "none of the
        options are in bold like they are on step 1 - it is NOT
        consistent"). Every label on this step used to override the
        shared `.label` class with Step 1's option-card treatment -
        text-base, font-semibold, gray-900 - because within this page a
        customer reads Step 1's choices and Step 2's fields as the same
        kind of thing, and they were rendering at different weights,
        sizes and colours.

        RESOLVED 2026-08-24 : that treatment was promoted INTO `.label`
        and the five overrides here retired as redundant. The comment
        that stood here recorded the divergence as a known one and left
        rolling it across the other tools to the operator ; he called it
        ("I'm trying to get the entire tools project to be consistent
        and to look the same"), so the labels below are bare `.label`
        and render exactly as they did before - verified by computed
        style, not assumed. There is now ONE definition, in
        src/client/styles/index.css, and this step is no longer a
        special case. Do not re-add an override here : changing the
        class is what changes a form label now.
      */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <label htmlFor="domainsRaw" className="label">
            Domain Names *
          </label>
          {/*
            Helper sits BETWEEN the label and the control, in the
            site's base-size prose colour : the unanimous sibling
            pattern (six occurrences across DNSStampGenerator,
            CAARecordGenerator and PasswordGenerator). The previous
            classes here were text-sm text-gray-500 plus a
            "trustico-field-desc" token that is not defined in any
            stylesheet, so this one description rendered smaller and
            fainter than every other helper on the site.
          */}
          <p className="text-body mb-2">
            Enter every domain name you want this SSL Certificate to secure, one
            per line. You can enter up to {MAX_DOMAINS_PER_CERTIFICATE} licensed
            domain names including wildcards, root domains and subdomains if
            they are included within the license. Anything you leave out will
            not be included within the SSL Certificate, and adding it afterwards
            means issuing a replacement SSL Certificate.
          </p>
          {/*
            rows={3} matches the multi-line list inputs on
            DNSStampGenerator (TLS Certificate hashes, bootstrap
            resolver addresses) and exactly fits the three-line
            placeholder. The previous rows={6} left a large empty box
            dominating the step for the common case of one or two
            domain names.
          */}
          <textarea
            id="domainsRaw"
            name="domainsRaw"
            rows={3}
            value={formData.domainsRaw}
            onChange={handleChange}
            placeholder={"example.com\nwww.example.com\n*.example.com"}
            className={`textarea font-mono ${errors.domainsRaw ? "border-red-500" : ""}`}
            disabled={loading || isResume}
          />
          {errors.domainsRaw && (
            <p className="error-text">{errors.domainsRaw}</p>
          )}
        </div>

        {/*
          flex flex-col on each paired cell, with grow on the
          helper : grid cells stretch to the tallest in their row, so
          without this the two controls in a row sit at different
          heights whenever their descriptions wrap to a different
          number of lines. Growing the helper pushes every control to
          the bottom of its cell, so the inputs line up across the row
          however the text reflows. The two dropdowns below are the
          pair this matters most for.
        */}

        {/*
          TWO dependent dropdowns, matching CSRGenerator's Key Type +
          Key Size / ECC Curve pattern exactly (operator ruling
          2026-08-16 : the same choice must be presented the same way in
          both tools). keyTypeId stays the single source of truth and
          the only thing submitted, so the server contract is unchanged
          - the algorithm is derived from it, and picking an algorithm
          selects that algorithm's first entry.

          The option sets deliberately still differ from CSRGenerator's
          (RSA 8192 here, P-521 there) : those are real capability
          differences between what the Certificate Authority will issue
          and what we can generate locally, not a styling inconsistency
          (same ruling).
        */}
        <div className="flex flex-col">
          <label htmlFor="keyAlgorithm" className="label">
            Key Type
          </label>
          <p className="text-body mb-2 grow">
            {isResume
              ? "Locked to the settings in your configuration file."
              : "RSA works with every browser and server. ECC gives the same protection with a smaller key, but a few older systems do not support it and may not be able to use it."}
          </p>
          <select
            id="keyAlgorithm"
            name="keyAlgorithm"
            value={selectedAlgorithm}
            onChange={handleAlgorithmChange}
            className="input"
            disabled={loading || isResume}
          >
            {KEY_ALGORITHMS.map((alg) => (
              <option key={alg.value} value={alg.value}>
                {alg.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label htmlFor="keyTypeId" className="label">
            {isEcc ? "ECC Curve" : "Key Size"}
          </label>
          <p className="text-body mb-2 grow">
            {isResume
              ? "Locked to match the key type above."
              : isEcc
                ? "P-256 suits almost every website. P-384 is stronger and slightly slower."
                : "2048 bits suits almost every website and is the recommended choice. Larger sizes are slower for every visitor connecting to your site, and some older devices do not support them."}
          </p>
          <select
            id="keyTypeId"
            name="keyTypeId"
            value={formData.keyTypeId}
            onChange={handleChange}
            className="input"
            disabled={loading || isResume}
          >
            {sizeOptions.map((kt) => (
              <option key={kt.id} value={kt.id}>
                {kt.sizeLabel}
              </option>
            ))}
          </select>
        </div>

        {/*
          NO in-form sub-heading above the credential fields (operator
          directive 2026-08-16 : removed). An earlier version split the
          form here the way CSRGenerator splits its own ("Key
          Configuration"). Do not reinstate it - the two fields below
          carry their own labels and descriptions, which is what the
          split was there to provide.
        */}

        {/*
          Both credential fields run FULL WIDTH. A real HMAC Key is a
          long base64 secret - the example placeholder alone is 47
          characters - and in a half-width column it scrolled sideways
          inside the input while the customer was trying to check it
          against an e-mail.
        */}
        <div className="md:col-span-2">
          <label htmlFor="eabKid" className="label">
            External Account Binding (EAB) Key Identifier *
          </label>
          {/* Every field carries its own description (operator rule) :
              this label was the one bare control in the form, while the
              HMAC field below it had text. */}
          <p className="text-body mb-2">
            The identifier for your Certificate as a Service (CaaS) license. It
            is sent to you by e-mail with your order and is also shown on your
            order details page. Treat this like a username and do not share it.
          </p>
          {/* Helper location per the operator (2026-08-13 content
              ruling) : the credentials arrive by e-mail AND sit on the
              order details page - the two EAB fields were the only
              Step 2 controls without a helper sentence, despite being
              the two a non-expert least understands. The
              one-service-only warning that used to end this sentence
              is not repeated here : it is already the second half of
              Step 1's own description, where the service is chosen. */}
          {/*
            2026-07-04 operator consistency directive ("show an example
            like abcdefghijk123456") : the placeholder is an
            example-style value, not instructional prose, matching
            every other tool page (the "www.example.com" idiom in
            src/client/pages/CSRGenerator.jsx). The fabricated example
            satisfies the shared EAB_REGEX base64url alphabet and the
            EAB_MIN_LENGTH floor from src/shared/issuance/config.js so
            it looks like a real Key Identifier without being one.
          */}
          <input
            type="text"
            id="eabKid"
            name="eabKid"
            value={formData.eabKid}
            onChange={handleChange}
            placeholder="AbCdEfGh1234"
            className={`input font-mono ${errors.eabKid ? "border-red-500" : ""}`}
            disabled={loading || !!loadedConfig}
            autoComplete="off"
            spellCheck="false"
          />
          {errors.eabKid && <p className="error-text">{errors.eabKid}</p>}
        </div>

        <div className="md:col-span-2">
          <label htmlFor="eabHmac" className="label">
            External Account Binding (EAB) HMAC Key *
          </label>
          <p className="text-body mb-2">
            The secret that goes with the identifier above. It is sent to you at
            the time of your order by e-mail and usually viewable for a short
            period in the ordering system. Treat it like a password and do not
            share it.
          </p>
          {/*
            2026-07-04 operator consistency directive : example-style
            placeholder, sibling of the Key Identifier one above but
            deliberately LONGER because real HMAC Keys are longer than
            Key Identifiers. Same fabricated-but-plausible posture :
            base64 alphabet with trailing "=" padding, valid against
            the shared EAB_REGEX ([A-Za-z0-9_-]+=*) in
            src/shared/issuance/config.js. Placeholders on
            type="password" inputs render unmasked by design, and this
            is not a real credential, so nothing sensitive is shown.
          */}
          <input
            type="password"
            id="eabHmac"
            name="eabHmac"
            value={formData.eabHmac}
            onChange={handleChange}
            placeholder="AbCd1234EfGh5678IjKl9012MnOp3456QrSt7890UvWxYz=="
            className={`input font-mono ${errors.eabHmac ? "border-red-500" : ""}`}
            disabled={loading || !!loadedConfig}
            autoComplete="off"
            spellCheck="false"
            data-lpignore="true"
            data-1p-ignore=""
          />
          {errors.eabHmac && <p className="error-text">{errors.eabHmac}</p>}
        </div>
      </div>

      {topError && (
        <Alert
          type="error"
          title="Unable to Start Issuance Request"
          live
          onClose={() => {
            setTopError(null);
            setTopErrorCode(null);
          }}
        >
          <p>{topError}</p>
          {/* The wrong-service hint (2026-08-13 go-public content
              ruling) : the most likely customer mistake is correct
              credentials against the WRONG Step 1 service - the
              upstream rejection then points at the one thing that is
              not wrong, so this page-owned sentence names the real
              first thing to check.

              GATED on the credential-rejection code (2026-08-27). It
              used to render under every start failure, and on a
              transient at the Certificate Authority it sent the
              operator hunting through Step 1 when the message itself
              said to retry. */}
          {/*
            Prose rules (operator directive 2026-08-15) : a control is
            never named in Title Case inside a sentence, and a sentence
            join is " - " rather than " : ". The instruction now
            describes the action ("start again") instead of quoting the
            button label back at the customer.
          */}
          {topErrorCode === EAB_REJECTED_CODE && (
            <p className="mt-2" data-testid="wrong-service-hint">
              Your credentials work with one service only, so if they are
              correct the most likely cause is the service chosen at Step 1.
              Start again and choose the service that matches the SSL
              Certificate license you bought.
            </p>
          )}
        </Alert>
      )}

      {/* 2026-08-13 go-public flattening : the equal-width two-column
          grid idiom every other tool page's paired action row shares
          (the earlier content-sized flex row rendered the two buttons
          at visibly different widths). */}
      {/* pt-2 removed (operator directive 2026-08-15 : spacing must be
          equal between elements) : the form's own space-y-6 already
          sets 24px above this row, and the extra 8px made the gap
          above the buttons the only one on the step that differed.
          Matches the action row in src/client/pages/PFXConverter.jsx
          exactly. */}
      <div className="flex justify-center">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
          {/*
          2026-07-04 operator consistency directive ("buttons should be
          standard Continue and Start Over") : the submit button is
          labelled exactly "Continue" on EVERY path - the former
          "Continue to Domain Verification" (fresh) and "Resume SSL
          Certificate Order" (resume mode) labels are retired. Resume
          mode still announces itself through the "Saved Issuance Request
          Loaded" Alert above, so no context is lost. The
          disabled prop is the requiredFieldsFilled presence gate (see
          its comment above the return) ; ActionButton additionally
          disables itself while loading.
        */}
          <ActionButton
            type="submit"
            variant="primary"
            disabled={!requiredFieldsFilled}
            loading={loading}
            loadingText={progressMessage || "Working..."}
            icon={DocumentPlusIcon}
          >
            Continue
          </ActionButton>
          {/*
          Operator directive 2026-07-05 ("Always there should be a
          Continue and Start Over button in EACH section") : the
          section's own Start Over, in the standard centered action
          row. type="button" is REQUIRED - the default button type
          inside a <form> is "submit", and Start Over must never
          trigger the issuance submit. Disabled while the submit is in
          flight : resetting mid-create would race the in-flight
          apiCreateSession/apiStartIssuance round-trips (the create
          response would re-populate the parent's session ref via
          onSessionCreated AFTER the reset had cleared it, leaving an
          orphan the reset was supposed to discard).
        */}
          <StartOverButton type="button" onReset={onReset} disabled={loading} />
        </div>
      </div>
    </form>
  );
}

export default Step2Configure;
